import { jsPDF } from "jspdf";
import QRCode from "qrcode";
import { getEducationalCaseViewerUrl } from "./educational-case-viewer-link";
import {
  getAssignmentThumbnailSource,
} from "./educational-assignment-thumbnail";

const PAGE_MARGIN = 36;
const HEADER_COLOR = [33, 118, 189];
const BORDER_COLOR = [218, 218, 218];

const displayValue = (value, fallback = "") => {
  if (value === null || value === undefined || value === "") return fallback;
  if (typeof value === "object") return value.name || value.label || fallback;
  return String(value);
};

const arrayBufferToBase64 = (buffer) => {
  const bytes = new Uint8Array(buffer);
  const chunkSize = 0x8000;
  let binary = "";

  for (let offset = 0; offset < bytes.length; offset += chunkSize) {
    binary += String.fromCharCode.apply(
      null,
      bytes.subarray(offset, offset + chunkSize),
    );
  }

  return window.btoa(binary);
};

const blobToDataUrl = (blob) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });

const loadImage = async (item) => {
  const source = getAssignmentThumbnailSource(item);
  if (!source) return null;
  if (source.startsWith("data:image/")) return source;

  const response = await fetch(source, { credentials: "omit" });
  if (!response.ok) throw new Error("Unable to load report thumbnail");
  return blobToDataUrl(await response.blob());
};

const addLabel = (doc, label, value, x, y, maxWidth) => {
  doc.setFont("helvetica", "bold");
  doc.text(`${label}:`, x, y);
  const labelWidth = doc.getTextWidth(`${label}: `);
  doc.setFont("helvetica", "normal");
  const lines = doc.splitTextToSize(displayValue(value, "N/A"), maxWidth - labelWidth);
  doc.text(lines, x + labelWidth, y);
  return Math.max(1, lines.length) * 14;
};

const addPageHeader = async (doc, report) => {
  const pageWidth = doc.internal.pageSize.getWidth();
  const leftWidth = pageWidth - PAGE_MARGIN * 2 - 180;
  let y = PAGE_MARGIN;

  doc.setFontSize(10);
  y += addLabel(doc, "Date", report.reportDate, PAGE_MARGIN, y, leftWidth);
  if (report.user) y += addLabel(doc, "User", report.user, PAGE_MARGIN, y, leftWidth);
  y += addLabel(doc, "Assignment", report.systemIdNumber, PAGE_MARGIN, y, leftWidth);
  y += addLabel(doc, "Title", report.title, PAGE_MARGIN, y, leftWidth);

  if (report.link) {
    const linkX = pageWidth - PAGE_MARGIN - 165;
    doc.setFont("helvetica", "bold");
    doc.text("Link:", linkX, PAGE_MARGIN);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(...HEADER_COLOR);
    const linkLines = doc.splitTextToSize(report.link, 165);
    linkLines.forEach((line, index) => {
      doc.textWithLink(line, linkX, PAGE_MARGIN + 14 + index * 11, {
        url: report.link,
      });
    });
    doc.setTextColor(0, 0, 0);

    try {
      const qrCode = await QRCode.toDataURL(report.link, { width: 180, margin: 1 });
      doc.addImage(qrCode, "PNG", linkX, PAGE_MARGIN + 45, 72, 72);
    } catch (error) {
      console.warn("Unable to add QR code to assignment PDF", error);
    }
  }

  return Math.max(y + 14, PAGE_MARGIN + 128);
};

const addTableHeader = (doc, y, includeComments) => {
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.setTextColor(...HEADER_COLOR);
  doc.text("Tumor Case", PAGE_MARGIN, y);
  doc.text("Diagnosis", PAGE_MARGIN + 150, y);
  if (includeComments) doc.text("Comments", PAGE_MARGIN + 450, y);
  doc.setTextColor(0, 0, 0);
  return y + 8;
};

const addCaseRow = async (doc, item, index, y, includeComments, linkOptions) => {
  const pageWidth = doc.internal.pageSize.getWidth();
  const diagnosisX = PAGE_MARGIN + 150;
  const commentsX = PAGE_MARGIN + 450;
  const diagnosisWidth = includeComments ? 290 : pageWidth - diagnosisX - PAGE_MARGIN;
  const commentsWidth = pageWidth - commentsX - PAGE_MARGIN;
  const diagnosis = [
    displayValue(item.tumorSite, "Unknown"),
    displayValue(item.tumorType, "Unknown"),
    displayValue(item.tumorSubType, "Unknown"),
  ].join(" - ");
  const diagnosisLines = doc.splitTextToSize(diagnosis, diagnosisWidth - 12);
  const commentLines = includeComments
    ? doc.splitTextToSize(displayValue(item.comments, ""), commentsWidth - 12)
    : [];
  const rowHeight = Math.max(64, diagnosisLines.length * 13 + 12, commentLines.length * 13 + 12);

  doc.setDrawColor(...BORDER_COLOR);
  doc.roundedRect(PAGE_MARGIN, y, 140, 60, 5, 5);
  doc.line(PAGE_MARGIN + 34, y, PAGE_MARGIN + 34, y + 60);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(14);
  doc.text(String(index + 1), PAGE_MARGIN + 17, y + 34, { align: "center" });

  try {
    const image = await loadImage(item);
    if (image) doc.addImage(image, PAGE_MARGIN + 38, y + 3, 99, 54, undefined, "FAST");
  } catch (error) {
    console.warn("Unable to add assignment thumbnail to PDF", error);
  }

  doc.rect(diagnosisX, y, diagnosisWidth, rowHeight);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.text(diagnosisLines, diagnosisX + 6, y + 15);

  if (includeComments) {
    doc.rect(commentsX, y, commentsWidth, rowHeight);
    doc.text(commentLines, commentsX + 6, y + 15);
  }

  try {
    const caseUrl = await getEducationalCaseViewerUrl(item, linkOptions);
    if (caseUrl) doc.link(PAGE_MARGIN, y, 140, 60, { url: caseUrl });
  } catch (error) {
    console.warn("Unable to add assignment case link to PDF", error);
  }

  return y + rowHeight;
};

// linkOptions supplies router/encryptData and an optional getCaseSlideIds fallback.
// Existing callers supplying cases[].caseUrl can continue to pass only the report.
export const createEducationalAssignmentPdf = async (report, linkOptions = {}) => {
  const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "a4" });
  const pageHeight = doc.internal.pageSize.getHeight();
  const includeComments = report.includeComments !== false;
  let y = await addPageHeader(doc, report);
  y = addTableHeader(doc, y, includeComments);

  const cases = Array.isArray(report.cases) ? report.cases : [];
  for (let index = 0; index < cases.length; index += 1) {
    if (y + 70 > pageHeight - PAGE_MARGIN) {
      doc.addPage();
      y = addTableHeader(doc, PAGE_MARGIN, includeComments);
    }
    y = await addCaseRow(doc, cases[index], index, y, includeComments, linkOptions);
  }

  const safeAssignmentId = displayValue(report.systemIdNumber, "assignment")
    .replace(/[^a-z0-9_-]+/gi, "_")
    .replace(/^_+|_+$/g, "");

  return {
    pdfBase64: arrayBufferToBase64(doc.output("arraybuffer")),
    pdfFileName: `${safeAssignmentId || "assignment"}.pdf`,
  };
};
